import { EnvironmentOutlined, FlagFilled } from '@ant-design/icons'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import { useSites } from '@api/useSites'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { PartnerStoreSearch } from '@components/OrderWizard/Panes/ControlPane/PaneComponents/PartnerStoreSearch'
import { DataSectionHeader, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { AutoComplete, Card, Input, Spin, Switch } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'

import { useDestinationsContext } from '../../../Contexts/DestinationsContext'
import { useOrderContainersContext } from '../../../Contexts/OrderContainersContext'
import { useWizardMaster } from '../../../Contexts/WizardMasterContext'

export function StoreChooser({
  storeList,
  title,
  destIndex,
  setSelectedStore,
  setIsEditMode,
  isReplace = false,
  activeTab = 0,
}) {
  const { setOrderEdited } = useWizardMaster()
  const { setSelectedTank, orderDestinations, getImpactedTankVolumes, setOrderDestinations } = useDestinationsContext()
  const { replaceContainers, updateVolumes, productGroupsApi } = useOrderContainersContext()
  const selectedStoreNumbers = orderDestinations.map((s) => s.store_number)
  const filteredStores = storeList.filter((t) => !selectedStoreNumbers.includes(t.store_number))
  const { useSplitPartnersQuery, updateSplitPartnersMutation } = useSites()
  const { useSiteByIdQuery } = useSiteMonitoring()
  const [newStoreId, setNewStoreId] = useState(null)
  const { data: newStore } = useSiteByIdQuery(newStoreId)
  const previousStore = useMemo(
    () => (activeTab > 0 ? orderDestinations?.[activeTab - 1] : null),
    [orderDestinations, activeTab]
  )
  const { data: splitPartnerSites, fetchStatus } = useSplitPartnersQuery(previousStore?.id)
  const splitPartners = splitPartnerSites?.filter((site) => !selectedStoreNumbers.includes(site.store_number))
  const onStoreSelect = async (value, id) => {
    const storeId = id || storeList.find((s) => s.store_number === value)?.id
    if (!storeId) return
    setNewStoreId(storeId)
  }
  useEffect(() => {
    if (newStore) {
      if (destIndex === 0) {
        productGroupsApi.setMinimumVolume(newStore.trailer_config_min_volume)
        productGroupsApi.setMaximumVolume(newStore.trailer_config_max_volume)
      }

      setSelectedTank({ ...newStore.tanks[0], store_number: newStore.store_number })
      if (isReplace) {
        setOrderEdited(true)
        replaceContainers(
          orderDestinations?.[destIndex]?.store_number,
          newStore,
          destIndex,
          orderDestinations,
          getImpactedTankVolumes,
          setOrderDestinations,
          updateVolumes,
          orderDestinations[0].store_number
        )
        setIsEditMode && setIsEditMode(false)
        return
      }
      setIsEditMode && setIsEditMode(false)
      setSelectedStore(destIndex, newStore, updateVolumes, replaceContainers)
    }
  }, [newStore])

  const handleUpdateSplitPartner = async (site, split_partner, cache) => {
    await updateSplitPartnersMutation.mutateAsync({
      site_id: previousStore.id,
      split_partner,
      split_partner_site_id: site.id,
    })
    cache.clearAll()
  }

  return (
    <Vertical>
      <Vertical bg='elevated' border='all' className='p-2' style={{ flex: 0, overflow: 'visible' }}>
        <Texto>{title}</Texto>
      </Vertical>
      <StoreSearch
        filteredStores={filteredStores}
        onStoreSelect={onStoreSelect}
        splitPartners={splitPartners}
        handleUpdateSplitPartner={handleUpdateSplitPartner}
        showPartnerSelect={activeTab !== 0}
        fetchStatus={fetchStatus}
      />
    </Vertical>
  )
}

function StoreSearch({
  onStoreSelect,
  filteredStores,
  splitPartners,
  handleUpdateSplitPartner,
  showPartnerSelect,
  fetchStatus,
}) {
  const [searchValue, setSearchValue] = useState('')
  const { user, scopes } = useSecurityContext()
  const isCarrier = scopes.is_carrier

  const { value: siteConfig, setValue: setSiteConfig } = useLocalStorage('OrderModal::SiteConfig', {
    includeCarrierManaged: true,
    user: user.username,
  })
  const renderItem = (store, index) => ({
    value: store.store_number,
    label: (
      <div
        className='border-bottom'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div className='bold-text' style={{ flex: 1 }}>
          {store.store_number}
        </div>
        {!isCarrier && (
          <div
            style={{
              color: 'var(--theme-color-4)',
              display: 'flex',
              flex: 2,
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {store?.managers?.length > 0 && (
              <>
                <FlagFilled />
                {store.managers?.length > 1 && 'Multiple Carriers'}
                {store.managers?.length === 1 && store.managers[0]}
              </>
            )}
          </div>
        )}

        <span style={{ display: 'flex', flex: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
          <EnvironmentOutlined className='mr-1' style={{ color: 'var(--theme-color-2)' }} />
          {`${store.city}, ${store.state}`}
        </span>
      </div>
    ),
    index,
  })
  const renderOptions = useMemo(() => {
    if (!filteredStores) return []
    return !siteConfig.includeCarrierManaged && !isCarrier
      ? filteredStores?.filter((store) => !store.managers.length).map(renderItem)
      : filteredStores?.map(renderItem)
  }, [filteredStores, siteConfig?.includeCarrierManaged])

  if (filteredStores?.length === 0) {
    return (
      <Card className='loading-store-container'>
        <Spin size='large' />
      </Card>
    )
  }

  return (
    <Vertical>
      {!isCarrier && (
        <div
          className='p-2'
          style={{
            display: 'flex',
            flex: 0,
            overflow: 'visible',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <p style={{ margin: 0, fontSize: '1.2em', fontWeight: 'bold', color: 'var(--gray-500)' }}>
            Include Carrier Managed
          </p>
          <Switch
            checked={siteConfig.includeCarrierManaged}
            onChange={() => setSiteConfig({ ...siteConfig, includeCarrierManaged: !siteConfig.includeCarrierManaged })}
          />
        </div>
      )}
      {!showPartnerSelect ? (
        <AutoComplete
          style={{
            width: '100%',
          }}
          value={searchValue}
          options={renderOptions}
          onSelect={(value, option) => {
            onStoreSelect(value, null)
            setSearchValue('')
          }}
          filterOption={(inputValue, option) => option.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1}
        >
          <Input.Search
            value={searchValue}
            placeholder='Search For Site'
            data-cy='site-search'
            onChange={(e) => {
              setSearchValue(e.target.value)
            }}
          />
        </AutoComplete>
      ) : (
        <PartnerStoreSearch
          isCarrier={isCarrier}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          splitPartners={splitPartners}
          handleUpdateSplitPartner={handleUpdateSplitPartner}
          onStoreSelect={onStoreSelect}
          fetchStatus={fetchStatus}
          siteConfig={siteConfig}
        />
      )}
    </Vertical>
  )
}
